import { loader } from '@monaco-editor/react';
import { default as MonacoEditor } from 'monaco-editor';
import { CodeEditorTheme } from '@app/types';

type IStandaloneThemeData = MonacoEditor.editor.IStandaloneThemeData;

async function initPeApis(monaco: typeof MonacoEditor) {
  const peApiFile = 'PeApi.d.ts';
  let typeDefinitions = '';
  try {
    typeDefinitions = await (await fetch(`/asset/${peApiFile}`)).text();
  } catch (error) {
    console.error('Unable to add PE APIs to CodeEditor', error);
  }

  monaco.languages.typescript.javascriptDefaults.addExtraLib(typeDefinitions, peApiFile);
}

function initThemes(monaco: typeof MonacoEditor) {
  const definitions: Record<CodeEditorTheme, IStandaloneThemeData> = {
    [CodeEditorTheme.githubLight]: {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '6a737d', fontStyle: 'italic' },
        { token: 'keyword', foreground: 'd73a49' },
        { token: 'string', foreground: '032f62' },
        { token: 'number', foreground: '005cc5' },
        { token: 'type', foreground: '6f42c1' },
        { token: 'function', foreground: '6f42c1' },
        { token: 'variable', foreground: '24292e' },
        { token: 'constant', foreground: '005cc5' }
      ],
      colors: {
        'editor.background': '#ffffff',
        'editor.foreground': '#24292e',
        'editor.lineHighlightBackground': '#f6f8fa',
        'editorCursor.foreground': '#24292e',
        'editor.selectionBackground': '#c8c8fa',
        'editorLineNumber.foreground': '#959da5',
        'editorGutter.background': '#ffffff'
      }
    },
    [CodeEditorTheme.solarizedLight]: {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '93a1a1', fontStyle: 'italic' },
        { token: 'keyword', foreground: '859900' },
        { token: 'string', foreground: '2aa198' },
        { token: 'number', foreground: 'd33682' },
        { token: 'type', foreground: 'b58900' },
        { token: 'function', foreground: '268bd2' },
        { token: 'variable', foreground: '657b83' },
        { token: 'constant', foreground: 'cb4b16' }
      ],
      colors: {
        'editor.background': '#fdf6e3',
        'editor.foreground': '#657b83',
        'editor.lineHighlightBackground': '#eee8d5',
        'editorCursor.foreground': '#657b83',
        'editor.selectionBackground': '#eee8d5',
        'editorLineNumber.foreground': '#93a1a1',
        'editorGutter.background': '#fdf6e3'
      }
    },
    [CodeEditorTheme.dracula]: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '6272a4', fontStyle: 'italic' },
        { token: 'keyword', foreground: 'ff79c6' },
        { token: 'string', foreground: 'f1fa8c' },
        { token: 'number', foreground: 'bd93f9' },
        { token: 'type', foreground: '8be9fd', fontStyle: 'italic' },
        { token: 'function', foreground: '50fa7b' },
        { token: 'variable', foreground: 'f8f8f2' },
        { token: 'constant', foreground: 'bd93f9' }
      ],
      colors: {
        'editor.background': '#282a36',
        'editor.foreground': '#f8f8f2',
        'editor.lineHighlightBackground': '#44475a',
        'editorCursor.foreground': '#f8f8f0',
        'editor.selectionBackground': '#44475a',
        'editorLineNumber.foreground': '#6272a4',
        'editorGutter.background': '#282a36'
      }
    },
    [CodeEditorTheme.oceanicNext]: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '65737e', fontStyle: 'italic' },
        { token: 'keyword', foreground: 'c594c5' },
        { token: 'string', foreground: '99c794' },
        { token: 'number', foreground: 'f99157' },
        { token: 'type', foreground: 'fac863' },
        { token: 'function', foreground: '6699cc' },
        { token: 'variable', foreground: 'cdd3de' },
        { token: 'constant', foreground: 'f99157' }
      ],
      colors: {
        'editor.background': '#1b2b34',
        'editor.foreground': '#cdd3de',
        'editor.lineHighlightBackground': '#29414f',
        'editorCursor.foreground': '#c0c5ce',
        'editor.selectionBackground': '#4f5b66',
        'editorLineNumber.foreground': '#65737e',
        'editorGutter.background': '#1b2b34'
      }
    }
  };
  Object.entries(definitions).forEach(([themeName, definition]) => {
    monaco.editor.defineTheme(themeName, definition);
  });
}

export async function initMonacoEditor() {
  const monaco = await loader.init();
  initThemes(monaco);
  await initPeApis(monaco);
}
